import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from './use-toast';

export const CATEGORIA_DEFEITO_OPTIONS = [
  { value: 'material', label: '🧱 Material', description: 'Qualidade ou especificação do material' },
  { value: 'execucao', label: '🔧 Execução', description: 'Processo ou técnica de aplicação' },
  { value: 'projeto', label: '📐 Projeto', description: 'Erro ou omissão no projeto' },
  { value: 'seguranca', label: '⚠️ Segurança', description: 'Risco de segurança ou NR' },
  { value: 'documentacao', label: '📋 Documentação', description: 'Falta ou inconsistência documental' },
  { value: 'outro', label: '❓ Outro', description: 'Não se enquadra nas categorias anteriores' },
] as const;

export type CategoriaDefeito = typeof CATEGORIA_DEFEITO_OPTIONS[number]['value'];

export interface QualityGateItem {
  id: string;
  contrato_id: string;
  contrato_nome: string;
  contrato_numero: string;
  data_execucao: string;
  quantidade_executada: number;
  unidade: string;
  tipo: 'execucao' | 'retrabalho';
  item_codigo: string | null;
  item_descricao: string | null;
  observacoes: string | null;
  localizacao_descricao: string | null;
  qualidade_status: 'pendente' | 'aprovada' | 'rejeitada' | null;
  qualidade_por: string | null;
  qualidade_em: string | null;
  qualidade_observacoes: string | null;
  status: 'pendente' | 'validado' | 'rejeitado' | 'em_apontamento' | 'aguardando_qualidade' | 'aguardando_planejamento' | 'disponivel_para_medicao' | 'vinculada_a_boletim';
  registrado_por: string;
  created_at: string;
  // evidência count (joined)
  evidencias_count?: number;
}

export interface QualityGateStats {
  total: number;
  pendentes: number;
  aprovadas: number;
  rejeitadas: number;
  taxaAprovacao: number;
}

/** Fetch all executions pending quality review across all contracts */
export function useQualityGateQueue() {
  return useQuery({
    queryKey: ['quality-gate-queue'],
    queryFn: async () => {
      // Fetch all executions that are still in pending status (quality not yet reviewed)
      const { data, error } = await supabase
        .from('emp_validacoes_execucao')
        .select(`
          id,
          contrato_id,
          data_execucao,
          quantidade_executada,
          unidade,
          tipo,
          planilha_item_id,
          observacoes,
          localizacao_descricao,
          qualidade_status,
          qualidade_por,
          qualidade_em,
          qualidade_observacoes,
          status,
          registrado_por,
          created_at
        `)
        .eq('status', 'aguardando_qualidade')
        .order('data_execucao', { ascending: false });

      if (error) throw error;

      // Fetch contract info for each unique contrato_id
      const items = data || [];
      const contratoIds = [...new Set(items.map((i: any) => i.contrato_id))];
      const planilhaItemIds = [...new Set(items.map((i: any) => i.planilha_item_id).filter(Boolean))];

      let contratoMap: Record<string, { nome: string; numero: string }> = {};
      if (contratoIds.length > 0) {
        const { data: contratos } = await supabase
          .from('emp_contratos')
          .select('id, nome, numero_contrato')
          .in('id', contratoIds);

        (contratos || []).forEach((c: any) => {
          contratoMap[c.id] = { nome: c.nome, numero: c.numero_contrato };
        });
      }

      // Fetch planilha item info for item_codigo/item_descricao
      let itemMap: Record<string, { codigo: string; descricao: string }> = {};
      if (planilhaItemIds.length > 0) {
        const { data: planItems } = await supabase
          .from('emp_planilha_itens')
          .select('id, codigo_item, descricao')
          .in('id', planilhaItemIds);

        (planItems || []).forEach((p: any) => {
          itemMap[p.id] = { codigo: p.codigo_item, descricao: p.descricao };
        });
      }

      return items.map((item: any) => ({
        ...item,
        contrato_nome: contratoMap[item.contrato_id]?.nome || '—',
        contrato_numero: contratoMap[item.contrato_id]?.numero || '—',
        item_codigo: item.planilha_item_id ? (itemMap[item.planilha_item_id]?.codigo || null) : null,
        item_descricao: item.planilha_item_id ? (itemMap[item.planilha_item_id]?.descricao || null) : null,
      })) as QualityGateItem[];
    },
    refetchInterval: 30_000, // refresh every 30s
  });
}

/** Fetch recently reviewed (last 90 days) for statistics */
export function useQualityGateHistory() {
  return useQuery({
    queryKey: ['quality-gate-history'],
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - 90);

      const { data, error } = await supabase
        .from('emp_validacoes_execucao')
        .select('qualidade_status, qualidade_em, contrato_id')
        .not('qualidade_status', 'is', null)
        .gte('qualidade_em', since.toISOString())
        .order('qualidade_em', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });
}

/** Approve or reject quality gate with mandatory category on rejection */
export function useQualityGateMutation() {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      execucaoId,
      contratoId,
      decision,
      observacoes,
      categoria,
    }: {
      execucaoId: string;
      contratoId: string;
      decision: 'aprovada' | 'rejeitada';
      observacoes: string;
      categoria?: CategoriaDefeito;
    }) => {
      if (!user) throw new Error('Não autenticado');
      if (decision === 'rejeitada' && (!observacoes.trim() || !categoria)) {
        throw new Error('Rejeição requer observação e categoria de defeito obrigatórias.');
      }

      const qualidade_observacoes = decision === 'rejeitada'
        ? `[${categoria?.toUpperCase()}] ${observacoes.trim()}`
        : observacoes.trim() || null;

      // Use the state machine RPC for quality transitions
      const novoStatus = decision === 'aprovada' ? 'aguardando_planejamento' : 'em_apontamento';
      const { error } = await supabase.rpc('emp_transicionar_execucao', {
        p_execucao_id: execucaoId,
        p_novo_status: novoStatus,
        p_observacoes: qualidade_observacoes,
        p_motivo_rejeicao: decision === 'rejeitada' ? qualidade_observacoes : null,
        p_certificado_evidencia_id: null,
      });

      if (error) throw error;

      // Se rejeitada: gerar glosa vinculada à execução
      // EV/PV/AC permanecem imutáveis — glosa atua apenas no valor faturável
      if (decision === 'rejeitada') {
        const descricaoGlosa = `Rejeição de qualidade [${categoria?.toUpperCase() || 'DEFEITO'}]: ${observacoes.trim()}`;
        const { error: glosaError } = await supabase
          .from('emp_glosas' as any)
          .insert({
            contrato_id: contratoId,
            execucao_id: execucaoId,
            tipo: 'qualidade',
            descricao: descricaoGlosa,
            valor_glosa: 0, // valor preenchido manualmente pelo gestor ao medição
            fundamentacao: `Categoria de defeito: ${categoria}. Registro automático via Quality Gate.`,
            status: 'pendente',
            categoria_defeito: categoria || null,
            created_by: user.id,
            // boletim_id é null — glosa pré-medição
          });
        // Não bloquear o fluxo se a glosa falhar (log apenas)
        if (glosaError) console.warn('[QualityGate] Falha ao criar glosa:', glosaError.message);
      }

      return { execucaoId, contratoId, decision };
    },
    onSuccess: (vars) => {
      qc.invalidateQueries({ queryKey: ['quality-gate-queue'] });
      qc.invalidateQueries({ queryKey: ['quality-gate-history'] });
      qc.invalidateQueries({ queryKey: ['emp-execucoes', vars.contratoId] });
      // Invalidar glosas do contrato afetado para refletir a nova glosa criada
      if (vars.decision === 'rejeitada') {
        qc.invalidateQueries({ queryKey: ['emp-glosas', vars.contratoId] });
        qc.invalidateQueries({ queryKey: ['emp-medicoes-glosas'] });
      }
      toast({
        title: vars.decision === 'aprovada' ? '✓ Qualidade aprovada' : '✗ Qualidade rejeitada',
        description: vars.decision === 'rejeitada'
          ? 'Rejeição registrada. Glosa pré-medição criada automaticamente.'
          : undefined,
      });
    },
    onError: (e: Error) => toast({
      title: 'Erro na validação',
      description: e.message,
      variant: 'destructive',
    }),
  });
}
